import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { clickNotification, getNotifications, getUnreadNotificationCount, markAllNotificationsRead, markNotificationRead } from 'API/notifications';
import type { CustomerNotification } from 'types/engagement.types';

export const UNREAD_POLL_MS = 60 * 1000;

export const notificationKeys = {
  all: ['notifications'] as const,
  unread: (token: string) => ['notifications', 'unread', token] as const,
  list: (token: string, limit: number) => ['notifications', 'list', token, limit] as const,
};

/**
 * Unread badge: polls every 60 s, but only while the tab is visible. react-query pauses
 * `refetchInterval` while the document is hidden when `refetchIntervalInBackground` is false
 * (focusManager listens to visibilitychange) and refetches once on return.
 */
export const unreadCountQueryOptions = (token: string) => ({
  queryKey: notificationKeys.unread(token),
  queryFn: getUnreadNotificationCount,
  refetchInterval: UNREAD_POLL_MS,
  refetchIntervalInBackground: false,
  refetchOnWindowFocus: true,
  staleTime: UNREAD_POLL_MS / 2,
  // A badge must never open an error dialog or hammer a missing endpoint.
  retry: false,
  enabled: !!token,
});

export const useUnreadCount = (token: string): number => {
  const { data } = useQuery(unreadCountQueryOptions(token));
  return typeof data === 'number' ? data : 0;
};

export const useNotificationList = (token: string, limit: number, enabled = true) =>
  useQuery({ queryKey: notificationKeys.list(token, limit), queryFn: () => getNotifications(limit), enabled: enabled && !!token, retry: false });

/** Only same-app paths are navigated in-app; anything else is a full navigation. */
export const isInternalCta = (url: string): boolean => url.startsWith('/') && !url.startsWith('//');

/** In-app paths may come with the router basename (/dashboard/…) — strip it for navigate(). */
export const toRouterPath = (url: string): string => (url.startsWith('/dashboard/') ? url.slice('/dashboard'.length) : url === '/dashboard' ? '/' : url);

export const useNotificationActions = () => {
  const queryClient = useQueryClient();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: notificationKeys.all });

  const read = useMutation({ mutationFn: (id: string) => markNotificationRead(id), onSettled: invalidate });
  const readAll = useMutation({ mutationFn: markAllNotificationsRead, onSettled: invalidate });
  const click = useMutation({ mutationFn: (id: string) => clickNotification(id), onSettled: invalidate });

  /** CTA: count the click first (the API marks it read), then go. Navigation never waits on failure. */
  const openCta = async (n: CustomerNotification, navigate: (path: string) => void) => {
    try {
      await click.mutateAsync(n._id);
    } catch {
      // Counting failed — the customer still gets where the button promised.
    }
    if (!n.ctaUrl) return;
    if (isInternalCta(n.ctaUrl)) navigate(toRouterPath(n.ctaUrl));
    else window.location.href = n.ctaUrl;
  };

  return { read, readAll, click, openCta };
};
